import React, {useMemo, useState} from "react";
import {getCurrentTaxYear} from "../../../../helpers/utils/Utils";
import {
    autoCalculateSummaryRow,
    getTaxRateYearValue, jsonCloneWithDates,
    updateSummarySectionRow
} from "../../../../helpers/utils/OrderDetailUtils";
import Modal from "../../../../components/common/form/Modal";
import {DataTable} from "../../../../components/common/datatable/DataTable";
import Form from "../../../../components/common/form/Form";
import {DataTableColumn} from "../../../../components/common/datatable/DataTableTypes";
import {useSearchAllJurisdictionQuery} from "../../../data/jurisdiction/jurisdictionApi";
import ButtonTray from "../../../../components/common/form/ButtonTray";
import {FaMagnifyingGlass} from "react-icons/fa6";


interface JurisdictionListModalProps {
    show: boolean;
    setShow: (value: boolean) => void;
    index: number;
    certificate: any;
    setCertificate: (certificate: any) => void;
    setUpdateTotalTaxValue?: (value: boolean) => void;
    showAccount: number;
    type: string;
    county: any;
    orderId?: number | null;
    setUpdatedAccountIndexList: any;
    setIsSavedResult: (value: boolean) => void;
}

export default function JurisdictionListModal({
                                                  show,
                                                  setShow,
                                                  index,
                                                  certificate,
                                                  setCertificate,
                                                  setUpdateTotalTaxValue,
                                                  showAccount,
                                                  type,
                                                  county,
                                                  orderId = null,
                                                  setUpdatedAccountIndexList,
                                                  setIsSavedResult
                                              }: JurisdictionListModalProps) {

    const [jurisdictionName, setJurisdictionName] = useState<any>();
    const [id, setId] = useState("");
    const [collectorName, setCollectorName] = useState("");
    const [collectorId, setCollectorId] = useState("");
    const [nonCollecting, setNonCollecting] = useState(false);
    const [inActive, isInActive] = useState(false);
    const [taxRateList, setTaxRateList] = useState<any[]>([]);
    const [note, setNote] = useState<any[]>([]);
    const taxYear = useMemo(() => getCurrentTaxYear(), []);

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();

        if (jurisdictionName !== "" && collectorName !== "") {
            // Deep clone to trigger state update
            let updatedCertificate = jsonCloneWithDates(certificate);

            const account = updatedCertificate.accounts[showAccount];
            const jurisdiction = account.jurisdictionList[index];
            const bond = account.bondList[index];

            if (type === 'jurisdiction') {
                jurisdiction.jurisdictionName = jurisdictionName;
                jurisdiction.jurisdictionId = id;
                jurisdiction.collectorName = collectorName;
                jurisdiction.collectorId = collectorId;
                jurisdiction.bill_year = taxYear;
                jurisdiction.tax_rate_List = taxRateList;
                jurisdiction.tax_rate = taxRateList == null ? null : getTaxRateYearValue(taxRateList, taxYear);
                jurisdiction.nonCollecting = nonCollecting;
                jurisdiction.inActive = inActive;
                jurisdiction.currentMonthDue = nonCollecting || inActive ? 0.0 : null;
                jurisdiction.nextMonthTaxDue = nonCollecting || inActive ? 0.0 : null;
                jurisdiction.baseTax = nonCollecting || inActive ? 0.0 : null;
                jurisdiction.baseDue = nonCollecting || inActive ? 0.0 : null;
                jurisdiction.placeHolder = false;
                jurisdiction.taxDataSource = 'EDITOR';
                jurisdiction.asOfDate = new Date();

                note && note.forEach(cur => {
                    if (!account.accountNotes.some((el: any) => el.comment === cur.comment)) {
                        account.accountNotes.push({
                            sort: cur.sort,
                            comment: cur.comment,
                            bold: cur.bold,
                            orderId,
                            saveAsCanned: false,
                            yellowHighlight: cur.yellowHighlight,
                            redColor: cur.redColor,
                            relationalId: id
                        });
                    }
                });

                updatedCertificate.accounts[showAccount].jurisdictionList[index] = jurisdiction;
                updatedCertificate.accounts[showAccount].accountNotes = account.accountNotes;
                updatedCertificate = updateSummarySectionRow(updatedCertificate, showAccount, index, taxYear);
                updatedCertificate = autoCalculateSummaryRow(updatedCertificate, showAccount);
                if (setUpdateTotalTaxValue != null) {
                    setUpdateTotalTaxValue(true);
                }
            } else {
                bond.collectorId = collectorId;
                bond.jurisdictionName = jurisdictionName;
                bond.jurisdictionId = id;
                bond.collectorName = collectorName;
                updatedCertificate.accounts[showAccount].bondList[index] = bond;

            }

            // Set cloned, updated object
            setCertificate(updatedCertificate);
            setUpdatedAccountIndexList((prev:any) => {
                if (!prev.includes(showAccount)) {
                    return [...prev, showAccount]; // return a new array
                }
                return prev; // no change needed
            });
            setIsSavedResult(false);
            setShow(false);

        }
    };

    function selectJurisdiction(props: any) {
        return (
            <span
                className={jurisdictionName === props.row.values.name ? 'active cursor-pointer ' : 'active cursor'}
                id={props.row.original.id + "1"}
            >{props.value}
            </span>
        );
    }

    const setJurisdiction = (row: any) => {
        setJurisdictionName(row['name']);
        setId(row['id']);
        setNonCollecting(row['nonCollecting']);
        isInActive(row['inActive']);
        setCollectorName(row?.['collector']?.['name']);
        setCollectorId(row?.['collector']?.['id']);
        setTaxRateList(row['taxRates']);
        setNote(row['certificateNotes']);
    };
    const columns: DataTableColumn[] = [
        {
            label: "Jurisdiction",
            accessor: "name",
            cell: selectJurisdiction,
            filterOptions: [{
                filterModalOption: {
                    filterModalPlaceHolder: "Enter Name...",
                    filterModalKey: "name",
                    filterModalIcon: <FaMagnifyingGlass size={14} className={''}/>
                },
                overrideFilterLabel: " ",
                overrideFilterAccessor: "name"
            }]
        },
        {label: "Collector Name", accessor: "collector.name", cell: selectJurisdiction},
    ];

    return (
        <>
            <Modal
                title={`Jurisdiction Selector`}
                show={show}
                setShow={setShow}
                className="w-2/5 bg-text-0 rounded-lg shadow-lg"
                fields={
                    <Form onSubmit={handleSubmit} fields={
                        <>
                            {/* Modal Body */}
                            <div className="modal-body px-4 pt-4">
                                <DataTable
                                    columns={columns}
                                    actions={{
                                        fetchAction: useSearchAllJurisdictionQuery,
                                    }}
                                    defaultFilter={{
                                        pageNumber: 0,
                                        pageSize: 5,
                                        countyId: county,
                                        type: "picker"
                                    }}
                                    appendFilterToUrl={false}
                                    renderOptions={{
                                        skipButtonSectionRender: true,
                                        skipFilterButtonRender: true,
                                        skipPaginationRender: false,
                                        skipUserSettingRender: true
                                    }}
                                    defaultSortOptions={{
                                        accessor: "name",
                                        direction: "asc"
                                    }}
                                    rowSelectionOptions={{
                                        enableRowSelection: true,
                                        onSelectionChange: (selectedRow: Set<any>) => {
                                            const [row] = Array.from(selectedRow);
                                            if (row) {
                                                setJurisdiction(row)
                                            }
                                        },
                                    }}
                                />
                            </div>

                            {/* Modal Footer */}
                            <div className="modal-footer border-t border-gray-300 px-4 py-2 flex justify-end gap-1">
                                <ButtonTray
                                    buttons={[
                                        {
                                            buttonProps: {
                                                btnText: "Cancel",
                                                onClick: () => setShow(false),
                                                type: "cancel",
                                            },
                                            buttonType: "button"
                                        },
                                        {
                                            buttonProps: {
                                                btnText: "Submit",
                                                type: "submit",
                                            },
                                            buttonType: "button",
                                        }
                                    ]}
                                    align={"end"}
                                    gap={2}
                                />
                            </div>
                        </>
                    }/>
                }
            />
        </>

    )
}
